/**
 * Presentation Service
 * 
 * Handles the generation of presentations from bookmark content
 */

import JSZip from 'jszip';
import { saveAs } from 'file-saver';

/**
 * Generate a PowerPoint (PPTX) presentation from selected bookmarks
 * @param {Array} bookmarks - Array of bookmark objects to include in the presentation
 * @param {string} [presentationName] - Optional custom name for the presentation
 * @returns {Promise} - Promise that resolves when the presentation is generated and downloaded
 */
export const generatePptxPresentation = async (bookmarks, presentationName = 'ThinkGPT Bookmarks') => {
  if (!bookmarks || bookmarks.length === 0) {
    throw new Error('No bookmarks provided for presentation');
  }

  try {
    console.log(`Starting presentation generation: ${presentationName}`);
    console.log(`Processing ${bookmarks.length} bookmarks`);
    
    // Use the cloud function to generate the PPTX
    // Get user email from local storage if available
    let email = undefined;
    try {
      const localStorageService = await import('./local-storage-service');
      email = localStorageService.getUserEmail();
      console.log(`User email found: ${email ? 'Yes' : 'No'}`);
    } catch (e) {
      console.warn('Could not import or get user email from local-storage-service:', e);
    }
    
    // Prepare the payload with the presentation name
    const payload = email ? { bookmarks, email, presentationName } : { bookmarks, presentationName };
    console.log('Sending request to cloud function...');
    
    // Set up a timeout to detect if the request is taking too long
    const timeoutPromise = new Promise((_, reject) => {
      setTimeout(() => {
        reject(new Error('Request timed out. The server might still be processing your presentation.'));
      }, 60000); // 60 second timeout
    });
    
    // Make the API request with a timeout
    const fetchPromise = fetch('https://us-central1-thinkgpt.cloudfunctions.net/generatePptxPresentation', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(payload),
    });
    
    // Race between the fetch and the timeout
    const response = await Promise.race([fetchPromise, timeoutPromise]);

    if (!response.ok) {
      let errorMessage = 'Failed to generate presentation';
      try {
        const errorData = await response.json();
        errorMessage = errorData.error || errorMessage;
      } catch (e) {
        console.error('Could not parse error response:', e);
      }
      throw new Error(errorMessage);
    }

    console.log('Response received, processing download...');
    
    // Get the blob from the response
    const blob = await response.blob();
    
    // Use file-saver to download the file with the custom name
    const fileName = `${presentationName.replace(/[^a-zA-Z0-9_\-\s]/g, '_')}.pptx`;
    console.log(`Saving file as: ${fileName}`);
    saveAs(blob, fileName);
    
    console.log('Presentation generation completed successfully');
    return Promise.resolve();
  } catch (error) {
    console.error('Error in PPTX generation:', error);
    return Promise.reject(error);
  }
};

/**
 * Generate an HTML presentation from selected bookmarks and package it as a ZIP file
 * @param {Array} bookmarks - Array of bookmark objects to include in the presentation
 * @returns {Promise} - Promise that resolves when the presentation is generated and downloaded
 */
export const generatePresentation = async (bookmarks) => {
  if (!bookmarks || bookmarks.length === 0) {
    throw new Error('No bookmarks provided for presentation');
  }

  // Create a new ZIP file
  const zip = new JSZip();
  const presentationDate = new Date().toLocaleDateString();
  const bookmarkCount = bookmarks.length;
  
  // Create HTML content for the presentation
  let indexHtml = `
  <!DOCTYPE html>
  <html lang="en">
  <head>
    <meta charset="UTF-8">
    <meta name="viewport" content="width=device-width, initial-scale=1.0">
    <title>ThinkGPT Bookmarks Presentation</title>
    <link rel="stylesheet" href="styles.css">
  </head>
  <body>
    <div class="presentation">
      <!-- Title Slide -->
      <section class="slide title-slide">
        <div class="content">
          <h1>ThinkGPT Bookmarks Presentation</h1>
          <p class="subtitle">Generated on ${presentationDate}</p>
          <p class="subtitle">Contains ${bookmarkCount} bookmark${bookmarkCount !== 1 ? 's' : ''}</p>
        </div>
      </section>
`;

  // Process each bookmark into a slide
  bookmarks.forEach((bookmark, index) => {
    // Clean up the text content
    const cleanedText = bookmark.textContent
      ? bookmark.textContent
          .replace(/\n{3,}/g, '\n\n')  // Replace 3+ newlines with just 2
          .trim()
      : 'No content available';
    
    // Create a slide for this bookmark
    indexHtml += `
      <!-- Slide ${index + 1} -->
      <section class="slide bookmark-slide" id="slide-${index + 1}">
        <div class="slide-number">${index + 1}/${bookmarkCount}</div>
        <div class="content">
          ${bookmark.platform ? `<div class="platform">Source: ${bookmark.platform}</div>` : ''}
          <h2>${bookmark.title || `Bookmark ${bookmark.id?.substring(0, 8) || index + 1}`}</h2>
          <div class="bookmark-content">${cleanedText.replace(/\n/g, '<br>')}</div>
          ${bookmark.url ? `<div class="source-url"><a href="${bookmark.url}" target="_blank">${bookmark.url}</a></div>` : ''}
        </div>
      </section>
`;
  });

  // Add navigation controls and close HTML
  indexHtml += `
      <div class="controls">
        <button id="prev-btn">Previous</button>
        <button id="next-btn">Next</button>
      </div>
    </div>
    <script src="presentation.js"></script>
  </body>
  </html>
  `;

  // Create CSS for styling
  const cssContent = `
  * {
    box-sizing: border-box;
    margin: 0;
    padding: 0;
  }
  
  body {
    font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Oxygen, Ubuntu, Cantarell, 'Open Sans', 'Helvetica Neue', sans-serif;
    line-height: 1.6;
    color: #333;
    background-color: #f5f5f5;
  }
  
  .presentation {
    width: 100%;
    height: 100vh;
    position: relative;
    overflow: hidden;
  }
  
  .slide {
    width: 100%;
    height: 100vh;
    display: none;
    padding: 40px;
    position: absolute;
    top: 0;
    left: 0;
    background-color: white;
  }
  
  .slide.active {
    display: block;
  }
  
  .title-slide {
    text-align: center;
    display: flex;
    flex-direction: column;
    justify-content: center;
  }
  
  .title-slide h1 {
    font-size: 3rem;
    margin-bottom: 2rem;
    color: #2c3e50;
  }
  
  .subtitle {
    font-size: 1.5rem;
    color: #7f8c8d;
    margin-bottom: 1rem;
  }
  
  .bookmark-slide {
    padding: 60px;
  }
  
  .bookmark-slide h2 {
    font-size: 2rem;
    margin-bottom: 1.5rem;
    color: #2c3e50;
  }
  
  .platform {
    font-style: italic;
    color: #7f8c8d;
    margin-bottom: 1rem;
  }
  
  .bookmark-content {
    font-size: 1.2rem;
    margin: 2rem 0;
    max-height: 60vh;
    overflow-y: auto;
    padding: 1rem;
    background-color: #f9f9f9;
    border-radius: 5px;
    white-space: pre-line;
  }
  
  .source-url {
    font-size: 0.8rem;
    margin-top: 2rem;
  }
  
  .source-url a {
    color: #3498db;
    text-decoration: none;
  }
  
  .source-url a:hover {
    text-decoration: underline;
  }
  
  .slide-number {
    position: absolute;
    bottom: 20px;
    right: 20px;
    font-size: 0.8rem;
    color: #95a5a6;
  }
  
  .controls {
    position: fixed;
    bottom: 20px;
    left: 50%;
    transform: translateX(-50%);
    z-index: 100;
  }
  
  .controls button {
    padding: 10px 20px;
    margin: 0 5px;
    background-color: #3498db;
    color: white;
    border: none;
    border-radius: 5px;
    cursor: pointer;
    font-size: 1rem;
  }
  
  .controls button:hover {
    background-color: #2980b9;
  }
  
  .controls button:disabled {
    background-color: #bdc3c7;
    cursor: not-allowed;
  }
  `;

  // Create JavaScript for navigation
  const jsContent = `
  document.addEventListener('DOMContentLoaded', () => {
    const slides = document.querySelectorAll('.slide');
    const prevBtn = document.getElementById('prev-btn');
    const nextBtn = document.getElementById('next-btn');
    let currentSlide = 0;
    
    // Initialize first slide
    slides[0].classList.add('active');
    updateNavButtons();
    
    // Navigation functions
    function showSlide(index) {
      slides.forEach(slide => slide.classList.remove('active'));
      slides[index].classList.add('active');
      currentSlide = index;
      updateNavButtons();
    }
    
    function updateNavButtons() {
      prevBtn.disabled = currentSlide === 0;
      nextBtn.disabled = currentSlide === slides.length - 1;
    }
    
    // Event listeners
    prevBtn.addEventListener('click', () => {
      if (currentSlide > 0) {
        showSlide(currentSlide - 1);
      }
    });
    
    nextBtn.addEventListener('click', () => {
      if (currentSlide < slides.length - 1) {
        showSlide(currentSlide + 1);
      }
    });
    
    // Keyboard navigation
    document.addEventListener('keydown', (e) => {
      if (e.key === 'ArrowRight' || e.key === 'Space') {
        if (currentSlide < slides.length - 1) {
          showSlide(currentSlide + 1);
        }
      } else if (e.key === 'ArrowLeft') {
        if (currentSlide > 0) {
          showSlide(currentSlide - 1);
        }
      }
    });
  });
  `;

  // Add files to the ZIP
  zip.file('index.html', indexHtml);
  zip.file('styles.css', cssContent);
  zip.file('presentation.js', jsContent);
  
  // Generate README file
  const readmeContent = `# ThinkGPT Bookmarks Presentation

This presentation was generated from ThinkGPT Bookmarks on ${presentationDate}.

## How to View

1. Extract all files from this ZIP archive
2. Open the index.html file in your web browser
3. Use the navigation buttons or arrow keys to move between slides

## Contents

This presentation contains ${bookmarkCount} bookmarks from your ThinkGPT collection.
`;
  
  zip.file('README.md', readmeContent);

  // Generate and download the ZIP file
  const content = await zip.generateAsync({ type: 'blob' });
  saveAs(content, `ThinkGPT_Bookmarks_${new Date().toISOString().split('T')[0]}.zip`);
  
  return true;
};

// Default export now includes both presentation generation methods
const presentationService = {
  generatePresentation,
  generatePptxPresentation
};

// For backward compatibility, export the generatePresentation function directly
export default presentationService;
